import React from 'react';
import { Filter, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FilterButtonProps {
  /** Whether the button is currently in active/open state */
  isActive: boolean;

  /** Whether any filters are currently applied */
  hasFilters: boolean;

  /** Click handler for the button */
  onClick: () => void;

  /** Click handler for the clear button (optional) */
  onClear?: (e: React.MouseEvent) => void;

  /** Label text to display in the button */
  label: string;

  /** Optional icon to replace the default Filter icon */
  icon?: React.ReactNode;

  /** Whether the button is being rendered in a mobile context */
  isMobile?: boolean;

  /** Additional CSS class names to apply to the button */
  className?: string;

  /** Whether to bold the text when filters are applied (defaults to true) */
  boldWhenFiltered?: boolean;

  /** Optional ref for the button element */
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * Reusable filter button component that maintains consistent styling across the application.
 * Used as a trigger for filter dropdowns with standardized active/inactive states.
 */
export function FilterButton({
  isActive,
  hasFilters,
  onClick,
  onClear,
  label,
  icon,
  isMobile = false,
  className = '',
  boldWhenFiltered = true,
  buttonRef,
}: FilterButtonProps) {
  // Handle clearing the filter (stop propagation to prevent dropdown toggle)
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClear) {
      onClear(e);
    }
  };

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-2',
        isActive
          ? 'bg-white text-blue-700 shadow-sm'
          : hasFilters
            ? 'bg-white text-blue-700 shadow-sm'
            : 'text-gray-700 hover:text-blue-700 hover:bg-white hover:shadow-sm',
        className
      )}
    >
      {icon || <Filter size={16} />}
      <span
        className={cn(
          'whitespace-nowrap',
          !isMobile && boldWhenFiltered && hasFilters && 'font-bold'
        )}
      >
        {label}
      </span>

      {/* Clear filter button - only when filters are active */}
      {hasFilters && onClear && (
        <X
          size={14}
          className="text-blue-400 hover:text-blue-600 transition-colors cursor-pointer ml-1"
          onClick={handleClear}
          aria-label="Clear filter"
        />
      )}
    </button>
  );
}
